export enum OTPStatus {
    Draft = 0,
    PendingValidation = 1,
    Active = 2,
    Completed = 3,
    PendingHcpValidation = 4,
    Aborted = 5,
    Appended = 6,
}

export enum OTPOutcome {
    None = 0,
    Completed = 1,
    Appended = 2,
    Aborted = 3,
}

export interface IOTPParameters {
    reductionPercentage: number;
    daysPerStep: number;
    targetFinalPercentage: number;
    targetFinalMME?: number;
    startDate?: string;
    notes?: string;
    selectedDrugID?: number;
    selectedRouteID?: number;
}

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

export interface IStepMedication {
    id: string;
    drugID: number;
    routeID: number;
    drugName?: string;
    routeName?: string;
    unitName?: string;
    dosageStrength: number;
    frequencyPerDay: number;
    conversionFactor?: number;
    timeOfDay?: TimeOfDay;
    notes?: string;
}

export interface ITaperingStep {
    id: number;
    stepNumber: number;
    startDate: string;
    endDate: string;
    durationDays: number;
    doseMME: number;
    dosePercentageOfOriginal: number;
    reductionFromPrevious: number;
    isManuallyEdited?: boolean;
    suggestedMME?: number;
    notes?: string;
    // Per-step drug override (if different from global selection)
    customDrugID?: number;
    customRouteID?: number;
    // Custom prescription values (legacy - single medication)
    customDosageStrength?: number;
    customFrequencyPerDay?: number;
    // Multiple medications per step
    medications?: IStepMedication[];
}

export interface IOTPSignature {
    id: number;
    otpID: number;
    hcpID: number;
    hcpName: string;
    role: string;
    signatureDate: string;
}

export interface IOTP {
    id: number;
    name?: string;
    patientID: number;
    patientName: string;
    savedMMECalculationID: number;
    originalMME: number;
    status: OTPStatus;
    statusDisplay: string;
    outcome: OTPOutcome;
    parameters: IOTPParameters;
    taperingSteps: ITaperingStep[];
    signatures: IOTPSignature[];
    totalDurationDays: number;
    taperPercentage: number;
    startDate?: string;
    endDate?: string;
    activatedDate?: string;
    patientAcknowledgmentNote?: string;
    createdDate: string;
    createdByHcpName: string;
    canEdit: boolean;
    canActivate: boolean;
    canSign: boolean;
    canValidate: boolean;
    delegatedToHcpID?: number;
    delegatedToHcpName?: string;
}

export interface IOTPTimelineEvent {
    id: number;
    action: string;
    actorHcpID: number | null;
    actorName: string;
    eventDate: string;
    details: Record<string, any> | null;
}

export interface IGenerateOTPRequest {
    patientID: number;
    savedMMECalculationID: number;
    parameters?: IOTPParameters;
    name?: string;
}

/**
 * Predefined reason codes for OTP extension or abortion
 * Based on clinical guidelines - mirrors backend OTPReasonCode enum
 */
export enum OTPReasonCode {
    Other = 0,
    PainFlare = 1,
    AdverseEffects = 2,
    PatientPreference = 3,
    WithdrawalSymptoms = 4,
    MedicalComplication = 5,
    TaperingRateTooFast = 6,
    NonCompliance = 7,
    ClinicalSituationChange = 8,
}

// Co-Medication Checklist Types
export enum CoMedicationWarningLevel {
    None = 'None',
    Caution = 'Caution',
    Warning = 'Warning',
    Severe = 'Severe',
}

export interface ICoMedicationChecklistItem {
    id?: number;
    medicationType: string;
    isPresent: boolean;
    notes?: string;
    warningLevel: CoMedicationWarningLevel | string;
    warningMessage?: string;
    warningAcknowledged: boolean;
    reviewedDate?: string;
    reviewedByHcpName?: string;
}

export interface ICoMedicationChecklist {
    otpId: number;
    items: ICoMedicationChecklistItem[];
    allItemsReviewed: boolean;
    allWarningsAcknowledged: boolean;
    isComplete: boolean;
}

export interface ISaveCoMedicationChecklistRequest {
    items: ICoMedicationChecklistItem[];
    acknowledgeAllWarnings: boolean;
}
